/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { useSearchParams } from 'react-router-dom-v5-compat'
import React, { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
    Message,
} from 'semantic-ui-react'
import { isProviderAPos } from '../../../services/Provider/Provider.service'
import useUser from '../../Shared/UserProvider/useUser'
import { LoadingPlaceholder } from './HeatMapOpeningTimes/LoadingPlaceholder'
import moment from 'moment'
import { OpenTimeSegment } from './OpenTimeSegment'
import { Virtuoso } from 'react-virtuoso'
import { useTranslation } from 'react-i18next'
import { useOpeningTimes } from './hooks'

const DEFAULT_PAGE_SIZE = '50'

const PosOpeningTime = () => {
    const [t] = useTranslation()
    const {user} = useUser()
    const [searchParams, setSearchParams] = useSearchParams()
    const queryClient = useQueryClient()
    const hasInitialized = useRef(false)
    const isPosUser = isProviderAPos(user.provider)

    const from = searchParams.get('from')
    const to = searchParams.get('to')
    const page = searchParams.get('page') || '1'
    const posIds = searchParams.get('posIds') ? searchParams.get('posIds').split(',') : []

    useEffect(() => {
        const next = new URLSearchParams(searchParams.toString())
        if (!searchParams.get('page')) {
            next.set('page', '1')
            setSearchParams(next, {replace: true})
        }
    }, [searchParams, setSearchParams])

    const {data: posOpeningTimes} = useOpeningTimes({
        from,
        to,
        page,
        size: DEFAULT_PAGE_SIZE,
        posIds,
        enabled: !!from && !!to && (isPosUser || !hasInitialized.current || posIds.length > 0),
    })

    useEffect(() => {
        if (isPosUser) {
            return
        }

        if (posOpeningTimes && posOpeningTimes.length > 0 && posIds.length === 0 && !hasInitialized.current) {
            const next = new URLSearchParams(searchParams.toString())
            const loadedPosIds = posOpeningTimes.map(pot => pot.pointOfSale.id)
            next.set('posIds', loadedPosIds.join(','))

            queryClient.setQueryData(
              ['metrics', 'pos-open-metrics', from, to, page, DEFAULT_PAGE_SIZE, loadedPosIds],
              posOpeningTimes,
            )

            setSearchParams(next, {replace: true})
        }

        if (posIds.length > 0 || posOpeningTimes) {
            hasInitialized.current = true
        }
    }, [posOpeningTimes, posIds.length, searchParams, setSearchParams, isPosUser, queryClient, from, to, page])

    if (!isPosUser && hasInitialized.current && posIds.length === 0) {
        return (
          <div
            css={css`
                display: flex;
                justify-content: center;
            `}
          >
              <Message compact>
                  {t('openings.opening_times.no_data_placeholder')}
              </Message>
          </div>
        )
    }

    if (!posOpeningTimes && (isPosUser || !hasInitialized.current || posIds.length > 0)) {
        const rowCount = from && to ? moment(to).diff(moment(from), 'days') + 1 : 1
        const placeholderCount = posIds.length > 0 ? posIds.length : 1
        return <>
            {Array.from({length: placeholderCount}).map((_, index) => (
              <LoadingPlaceholder key={index} rowCount={rowCount}/>
            ))}
        </>
    }

    if (posOpeningTimes && posOpeningTimes.length === 0) {
        return (
          <div
            css={css`
                display: flex;
                justify-content: center;
            `}
          >
              <Message compact>
                  Aucun point de vente avec des données d'ouverture dans la période.
              </Message>
          </div>
        )
    }

    return (
      <Virtuoso
        style={{height: `${posOpeningTimes.length * 420}px`}}
        useWindowScroll
        data={posOpeningTimes}
        initialItemCount={2}
        increaseViewportBy={{top: 300, bottom: 800}}
        overscan={300}
        itemContent={(index, posOpeningTime) => (
          <div css={css`
              margin-bottom: 1rem;
          `}>
              {posOpeningTime && <OpenTimeSegment
                key={posOpeningTime.pointOfSale.id}
                title={posOpeningTime.pointOfSale.name}
                openingsTimes={posOpeningTime}
                isWeekly={false}
                onClose={!isProviderAPos(user.provider) ? () => {
                    const next = new URLSearchParams(searchParams.toString())
                    const nextPosIds = next.get('posIds') ? next.get('posIds').split(',') : []
                    const filteredPosIds = nextPosIds.filter(id => id !== posOpeningTime.pointOfSale.id)
                    if (filteredPosIds.length > 0) {
                        next.set('posIds', filteredPosIds.join(','))
                    } else {
                        next.delete('posIds')
                    }
                    next.set('page', '1')
                    setSearchParams(next)
                } : undefined}
              />
              }
          </div>
        )}
      />
    )
}

export { PosOpeningTime }
